import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  request,
  type InboxCounts,
  type InboxList,
  type InboxReplyResult,
  type InboxThreadDetail,
} from './client';

/**
 * The enact@ inbox (API 051): mail to the general mailbox that answers no
 * record — someone writing without filling in a form. Listing and opening are
 * audited reads; the counts are not, which is why the rail asks for them alone.
 */

export type InboxFilter = 'open' | 'done' | 'all';
export const INBOX_PAGE_SIZE = 50;

/** Open and unread, for the Messages rail's badge. */
export const useInboxCounts = (enabled: boolean) =>
  useQuery({
    queryKey: ['inbox', 'counts'],
    queryFn: () => request<InboxCounts>('/inbox/counts'),
    enabled,
    refetchInterval: 60_000,
  });

export const useInbox = (status: InboxFilter, page: number) =>
  useQuery({
    queryKey: ['inbox', 'list', status, page],
    queryFn: () => request<InboxList>(`/inbox/?status=${status}&page=${page}&pageSize=${INBOX_PAGE_SIZE}`),
    placeholderData: keepPreviousData,
  });

export const useInboxThread = (id: string | null) =>
  useQuery({
    queryKey: ['inbox', 'thread', id],
    queryFn: () => request<InboxThreadDetail>(`/inbox/${id}`),
    enabled: id !== null,
  });

function useInboxMutation<B, R>(path: (id: string) => string, method: 'POST' | 'PATCH') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: B }) => request<R>(path(id), { method, body }),
    // A reply or a status change moves the conversation between Open and Done.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['inbox'] }),
  });
}

export const useInboxReply = () =>
  useInboxMutation<{ body: string }, InboxReplyResult>((id) => `/inbox/${id}/replies`, 'POST');

export const useInboxStatus = () =>
  useInboxMutation<{ status: 'open' | 'done' }, unknown>((id) => `/inbox/${id}`, 'PATCH');
